// src/ui/screens/post/WantScreen.jsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../../firebase'; // adjust path
import UserAvatar from '../../../components/UserAvatar';
import {
  StatusWantService,
  StatusWantType,
} from '../../../services/statusWantService';
import FirebaseChatService from '../../../services/chat/firebaseChatService';

const kOrange = '#FF8C08';
const kBg = '#08090B';
const kCard = '#23262C';

// Desktop layout: matches the `lg` (1024px) Tailwind breakpoint used elsewhere.
const DESKTOP_QUERY = '(min-width: 1024px)';

// Resolves the conversation id whether buildConversationId is an instance
// method, a static on the class, or missing entirely.
function buildConversationId(a, b) {
  const svc = FirebaseChatService;
  if (typeof svc?.buildConversationId === 'function') {
    return svc.buildConversationId(a, b);
  }
  if (typeof svc?.constructor?.buildConversationId === 'function') {
    return svc.constructor.buildConversationId(a, b);
  }
  // Fallback: same uid pair always yields the same id, regardless of who opens it
  return [a, b].sort().join('_');
}

/**
 * Detail viewer for a user's active "Want" post(s), posted by
 * AddWantScreen. Rendered as an overlay by PostScreen.
 *
 * Props:
 *  - uid            owner of the wants to show
 *  - onClose(ids)   called with the doc ids viewed this session
 *  - onOpenProfile  called with the owner's uid
 *  - onOpenChat     called with { conversationId, receiverId,
 *                   currentUserId, title, avatarUrl, initialMessage }
 *
 * Swipe horizontally between multiple active wants from the same user.
 * Marks each want as viewed by the current user (unless they're the
 * owner), and lets the owner see who has viewed it.
 *
 * Desktop (>=1024px): the viewer is a centered panel. When the want has
 * images they sit in a left column with the details on the right; prev/next
 * arrows flank the panel when there are several wants, and clicking the
 * dark backdrop closes it.
 */
export default function WantViewerScreen({
  uid,
  onClose,
  onOpenProfile,
  onOpenChat,
}) {
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [index, setIndex] = useState(0);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [snack, setSnack] = useState(null);
  const [imageViewer, setImageViewer] = useState(null); // { images, index }

  // On web, auth.currentUser can be null until Firebase restores the
  // session, so track it instead of reading it once at render time.
  const [authReady, setAuthReady] = useState(!!auth.currentUser);
  const [myUid, setMyUid] = useState(auth.currentUser?.uid ?? null);

  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia(DESKTOP_QUERY).matches
  );

  const viewedThisSession = useRef(new Set());
  const touchStartX = useRef(0);
  const snackTimer = useRef(null);

  const isOwner = !!uid && uid === myUid;

  // ── Viewport ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setMyUid(user?.uid ?? null);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    return () => {
      if (snackTimer.current) clearTimeout(snackTimer.current);
    };
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────

  const ownerName =
    ownerProfile?.displayName ?? ownerProfile?.name ?? 'User';

  const ownerUsername = (() => {
    const u = ownerProfile?.username?.toString() ?? '';
    if (!u) return '';
    return u.startsWith('@') ? u : `@${u}`;
  })();

  const ownerAvatarUrl =
    ownerProfile?.profileImage && ownerProfile.profileImage.toString().length > 0
      ? ownerProfile.profileImage.toString()
      : ownerProfile?.photoURL ?? null;

  const currentTimeLabel = (() => {
    if (docs.length === 0 || index >= docs.length) return '';
    const ts = docs[index].data()?.createdAt;
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return StatusWantService.relativeTime(date);
  })();

  const showSnack = (msg, isError = false) => {
    setSnack({ msg, isError });
    if (snackTimer.current) clearTimeout(snackTimer.current);
    snackTimer.current = setTimeout(() => setSnack(null), 3000);
  };

  // Same as Flutter's Navigator.pop(_viewedThisSession.toList()): hand the
  // viewed ids back to PostScreen so the rings grey out immediately.
  const handleClose = useCallback(() => {
    if (onClose) onClose(Array.from(viewedThisSession.current));
  }, [onClose]);

  const markCurrentViewed = (docList = docs, idx = index) => {
    if (isOwner || docList.length === 0) return;
    const current = docList[idx];
    if (!current || !current.id) return;
    const docId = current.id;
    // Grey the segment out right away — the write is fire-and-forget.
    viewedThisSession.current.add(docId);
    StatusWantService.markViewed(StatusWantType.want, docId);
  };

  // ── Load ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!authReady) return;

    if (!uid) {
      console.warn('[WantViewer] called with no uid');
      setLoadError('No user specified.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [activeDocs, profileSnap] = await Promise.all([
          StatusWantService.activeDocsForUser(StatusWantType.want, uid),
          getDoc(doc(db, 'users', uid)).catch((e) => {
            console.warn('[WantViewer] profile load failed', e);
            return null;
          }),
        ]);

        if (cancelled) return;

        // Open at the first want this user hasn't seen yet — same as
        // WhatsApp — falling back to the first one if all are viewed.
        let startIndex = 0;
        if (!isOwner) {
          for (let i = 0; i < activeDocs.length; i++) {
            const seen = await StatusWantService.hasViewed(
              StatusWantType.want,
              activeDocs[i].id
            );
            if (!seen) {
              startIndex = i;
              break;
            }
          }
        }

        if (cancelled) return;

        setDocs(activeDocs);
        setIndex(startIndex);
        setOwnerProfile(
          profileSnap && profileSnap.exists() ? profileSnap.data() : null
        );

        if (activeDocs.length > 0) {
          markCurrentViewed(activeDocs, startIndex);
        }
      } catch (e) {
        console.error('[WantViewer] load failed', e);
        if (!cancelled) {
          setLoadError(e?.message ?? 'Could not load want.');
        }
      } finally {
        // Always clear the spinner.
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, authReady, myUid]);

  // Load like state when index changes
  useEffect(() => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    let cancelled = false;
    const docId = current.id;
    (async () => {
      const liked = await StatusWantService.hasLiked(
        StatusWantType.want,
        docId
      );
      if (!cancelled) setIsLiked(liked);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, docs.length]);

  // ── Likes ────────────────────────────────────────────────────────────

  const toggleLike = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    const docId = current.id;
    const data = current.data();
    const imageUrls = data?.imageUrls ?? [];
    const thumbnailUrl = imageUrls.length > 0 ? imageUrls[0] : null;

    const optimistic = !isLiked;
    setIsLiked(optimistic);
    try {
      const result = await StatusWantService.toggleLike(
        StatusWantType.want,
        docId,
        { ownerUid: uid, thumbnailUrl }
      );
      setIsLiked(result);
    } catch (e) {
      console.warn('[WantViewer] toggleLike failed', e);
      setIsLiked(!optimistic);
    }
  };

  // ── Share ────────────────────────────────────────────────────────────

  const handleShare = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    const docId = current.id;
    const shareText = `Check out ${
      isOwner ? 'my' : `${ownerName}'s`
    } want on Filatom!\nhttps://filatom.com/want/${docId}`;

    try {
      if (navigator.share) {
        await navigator.share({ text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
      await StatusWantService.incrementShareCount(
        StatusWantType.want,
        docId
      );
    } catch (_) {
      // Best-effort — ignore share-sheet cancellations/errors.
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────

  const deleteCurrentWant = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    const docId = current.id;
    try {
      await deleteDoc(doc(db, 'wants', docId));
      const newDocs = docs.filter((d) => d.id !== docId);
      const newIndex =
        newDocs.length > 0 && index >= newDocs.length
          ? newDocs.length - 1
          : index;
      setDocs(newDocs);
      setIndex(newIndex);
      showSnack('Want deleted');
      if (newDocs.length === 0) {
        setTimeout(() => handleClose(), 500);
      }
    } catch (_) {
      showSnack('Failed to delete want', true);
    }
  };

  // ── Viewers ──────────────────────────────────────────────────────────

  const openViewers = async () => {
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;
    try {
      const list = await StatusWantService.viewersFor(
        StatusWantType.want,
        current.id
      );
      setViewers(list);
    } catch (e) {
      console.warn('[WantViewer] viewersFor failed', e);
      setViewers([]);
    }
    setShowViewers(true);
  };

  // ── Message (mirrors ListingPage._handleFilatomChat) ────────────────

  const handleMessageTap = () => {
    if (!myUid) {
      showSnack('Sign in to start a chat', true);
      return;
    }
    if (isOwner) {
      showSnack('This is your own want');
      return;
    }
    if (docs.length === 0) return;
    const current = docs[index];
    if (!current || !current.id) return;

    const data = current.data();
    const details = data?.details?.toString() ?? '';
    const autofillMessage = `Hey! From your want posted for "${details}", can I help you?`;

    const conversationId = buildConversationId(myUid, uid);

    if (onOpenChat) {
      onOpenChat({
        conversationId,
        receiverId: uid,
        currentUserId: myUid,
        title: ownerName,
        avatarUrl: ownerAvatarUrl,
        initialMessage: autofillMessage,
      });
    }
  };

  // ── Open owner profile ───────────────────────────────────────────────

  /** Only reachable when viewing someone else's want. */
  const openOwnerProfile = () => {
    if (isOwner) return;
    if (onOpenProfile) onOpenProfile(uid);
  };

  // ── Paging ───────────────────────────────────────────────────────────

  const goToPage = (i) => {
    setIndex(i);
    markCurrentViewed(docs, i);
  };

  // ── Render ───────────────────────────────────────────────────────────

  const overlayBase = {
    position: 'fixed',
    inset: 0,
    zIndex: 100,
    background: kBg,
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...overlayBase,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: `2px solid ${kOrange}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error or nothing active — show a message instead of hanging/blank.
  if (loadError || docs.length === 0) {
    return (
      <div
        style={{
          ...overlayBase,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
        }}
      >
        <p
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: 14,
            textAlign: 'center',
            margin: 0,
          }}
        >
          {loadError ? `Couldn't load want: ${loadError}` : 'No active want.'}
        </p>
        <button
          onClick={handleClose}
          style={{
            background: kOrange,
            border: 'none',
            borderRadius: 20,
            padding: '8px 20px',
            color: '#000',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    );
  }

  const currentDoc = docs[index];
  const data = currentDoc.data();
  const imageUrls = data?.imageUrls ?? [];
  const budget = data?.budget;
  const isFlexible = data?.isBudgetFlexible === true;

  // Desktop only: images left, details right (single column without images).
  const twoCol = isDesktop && imageUrls.length > 0;

  const rootStyle = isDesktop
    ? {
        ...overlayBase,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
      }
    : {
        ...overlayBase,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      };

  const panelStyle = isDesktop
    ? {
        width: 'min(1000px, calc(100vw - 160px))',
        height: 'min(92vh, 820px)',
        background: '#0E1013',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }
    : {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      };

  const sideArrowStyle = {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  };

  return (
    <div
      style={rootStyle}
      onClick={
        isDesktop
          ? (e) => {
              // Click on the dark backdrop closes the viewer.
              if (e.target === e.currentTarget) handleClose();
            }
          : undefined
      }
    >
      {/* Desktop: previous arrow (spacer keeps the panel centered) */}
      {isDesktop && (
        <div style={{ width: 48, flexShrink: 0 }}>
          {docs.length > 1 && index > 0 && (
            <button
              onClick={() => goToPage(index - 1)}
              aria-label="Previous want"
              style={sideArrowStyle}
            >
              ‹
            </button>
          )}
        </div>
      )}

      <div style={panelStyle}>
        {/* Top bar */}
        <div
          style={{
            padding: isDesktop ? '16px 24px' : '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: kCard,
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ←
          </button>

          <div
            onClick={openOwnerProfile}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: isOwner ? 'default' : 'pointer',
              minWidth: 0,
            }}
          >
            <UserAvatar size={32} imageUrl={ownerAvatarUrl} name={ownerName} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isOwner ? 'Your Want' : ownerName}
                </span>
                {currentTimeLabel && (
                  <>
                    <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: 13 }}>
                      ·
                    </span>
                    <span
                      style={{
                        color: 'rgba(255,255,255,0.38)',
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {currentTimeLabel}
                    </span>
                  </>
                )}
              </div>
              {!isOwner && ownerUsername && (
                <div
                  style={{
                    color: 'rgba(255,255,255,0.54)',
                    fontSize: 11.5,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ownerUsername}
                </div>
              )}
            </div>
          </div>

          {isOwner && (
            <button
              onClick={openViewers}
              style={{
                background: kCard,
                border: 'none',
                borderRadius: 16,
                padding: '8px 12px',
                color: kOrange,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              👁 Views
            </button>
          )}
        </div>

        {/* Page dots */}
        {docs.length > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              paddingBottom: 8,
            }}
          >
            {docs.map((_, i) => (
              <div
                key={i}
                onClick={() => goToPage(i)}
                style={{
                  width: i === index ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: i === index ? kOrange : 'rgba(255,255,255,0.24)',
                  transition: 'width 0.2s',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        )}

        {/* Content — swipeable */}
        <div
          style={{ flex: 1, overflowY: 'auto', touchAction: 'pan-y' }}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            const deltaX = e.changedTouches[0].clientX - touchStartX.current;
            if (deltaX < -60 && index < docs.length - 1) goToPage(index + 1);
            else if (deltaX > 60 && index > 0) goToPage(index - 1);
          }}
        >
          <div
            style={
              isDesktop
                ? {
                    padding: '8px 32px 32px',
                    display: 'grid',
                    gridTemplateColumns: twoCol
                      ? 'minmax(0, 1.1fr) minmax(0, 1fr)'
                      : 'minmax(0, 640px)',
                    justifyContent: 'center',
                    columnGap: 32,
                    alignItems: 'start',
                  }
                : { padding: '4px 16px 32px' }
            }
          >
            {/* Major heading */}
            <h2
              style={{
                color: '#fff',
                fontSize: 19,
                fontWeight: 800,
                lineHeight: 1.25,
                margin: '0 0 14px 0',
                ...(isDesktop ? { gridColumn: '1 / -1', fontSize: 24 } : {}),
              }}
            >
              Am looking for this;
            </h2>

            {imageUrls.length > 0 && (
              <div>
                <div
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  A look-alike of this
                </div>
                <div
                  style={{
                    borderRadius: 18,
                    overflow: 'hidden',
                    height: isDesktop ? 400 : 220,
                    marginBottom: 16,
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                  }}
                >
                  {imageUrls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      onClick={() =>
                        setImageViewer({ images: imageUrls, index: i })
                      }
                      style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        flexShrink: 0,
                        scrollSnapAlign: 'start',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              {/* Category chip */}
              <div
                style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  background: 'rgba(255,140,8,0.12)',
                  border: '1px solid rgba(255,140,8,0.35)',
                  borderRadius: 20,
                  color: kOrange,
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                {data?.category ?? ''} · {data?.subcategory ?? ''}
              </div>

              {/* Details */}
              <p
                style={{
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  margin: '0 0 18px 0',
                }}
              >
                {data?.details ?? ''}
              </p>

              {/* Info rows */}
              <InfoRow
                icon="💰"
                label="Budget"
                value={
                  isFlexible
                    ? 'Flexible'
                    : budget != null
                    ? `UGX ${formatMoney(budget)}`
                    : '—'
                }
              />
              <InfoRow
                icon="📍"
                label="Location"
                value={`${data?.division ?? ''}, ${data?.district ?? ''}`}
              />
              <InfoRow
                icon="⏱"
                label="Expires in"
                value={`${data?.expiryDays ?? '—'} days from posting`}
              />
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            padding: isDesktop
              ? '12px 24px 16px'
              : '8px 16px calc(env(safe-area-inset-bottom, 0px) + 12px)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div
            onClick={isOwner ? undefined : handleMessageTap}
            style={{
              flex: 1,
              height: 44,
              padding: '0 16px',
              background: kCard,
              borderRadius: 22,
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              color: 'rgba(255,255,255,0.38)',
              fontSize: 14,
              cursor: isOwner ? 'default' : 'pointer',
            }}
          >
            {isOwner ? 'Message' : 'Message...'}
          </div>

          <button
            onClick={toggleLike}
            style={{
              background: 'none',
              border: 'none',
              color: isLiked ? '#FF1493' : 'rgba(255,255,255,0.7)',
              fontSize: 26,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {isLiked ? '♥' : '♡'}
          </button>

          <button
            onClick={handleShare}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              fontSize: 24,
              cursor: 'pointer',
              padding: 0,
            }}
          >
            ➤
          </button>

          {isOwner && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.7)',
                fontSize: 24,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              🗑
            </button>
          )}
        </div>

        {/* Snackbar */}
        {snack && (
          <div
            style={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: snack.isError ? '#ff5252' : kOrange,
              color: '#fff',
              padding: '12px 20px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              zIndex: 1000,
            }}
          >
            {snack.msg}
          </div>
        )}

        {/* Viewers sheet */}
        {showViewers && (
          <div
            onClick={() => setShowViewers(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: isDesktop ? 'center' : 'flex-end',
              justifyContent: isDesktop ? 'center' : undefined,
              zIndex: 110,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: isDesktop ? 480 : undefined,
                background: '#16181C',
                borderRadius: isDesktop ? 20 : '20px 20px 0 0',
                padding: 16,
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <h3
                style={{
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  margin: '0 0 12px 0',
                }}
              >
                {viewers.length} {viewers.length === 1 ? 'view' : 'views'}
              </h3>
              {viewers.length === 0 ? (
                <p
                  style={{
                    color: 'rgba(255,255,255,0.38)',
                    fontSize: 13,
                    padding: '20px 0',
                  }}
                >
                  No views yet
                </p>
              ) : (
                viewers.map((v, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 0',
                    }}
                  >
                    <UserAvatar
                      size={34}
                      imageUrl={v.photoUrl}
                      name={v.displayName}
                    />
                    <span style={{ color: '#fff', fontSize: 13 }}>
                      {v.displayName ?? 'User'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Delete dialog */}
        {showDeleteDialog && (
          <div
            onClick={() => setShowDeleteDialog(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              zIndex: 110,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: kCard,
                borderRadius: 16,
                padding: '24px 20px 16px',
                maxWidth: 320,
                width: '100%',
              }}
            >
              <h3
                style={{
                  color: '#fff',
                  fontSize: 17,
                  fontWeight: 700,
                  margin: '0 0 10px 0',
                  textAlign: 'center',
                }}
              >
                Delete Want
              </h3>
              <p
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 14,
                  lineHeight: 1.5,
                  textAlign: 'center',
                  margin: '0 0 20px 0',
                }}
              >
                This want will be permanently removed.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: '8px 12px',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    deleteCurrentWant();
                  }}
                  style={{
                    background: 'rgba(255,82,82,0.15)',
                    border: 'none',
                    color: '#ff5252',
                    fontWeight: 700,
                    fontSize: 14,
                    borderRadius: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Full-screen image viewer */}
        {imageViewer && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: '#000',
              zIndex: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={imageViewer.images[imageViewer.index]}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
            {imageViewer.images.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  background: 'rgba(0,0,0,0.45)',
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {imageViewer.index + 1}/{imageViewer.images.length}
              </div>
            )}
            <button
              onClick={() => setImageViewer(null)}
              style={{
                position: 'absolute',
                top: 12,
                right: 16,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.45)',
                border: 'none',
                color: '#fff',
                fontSize: 18,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Desktop: next arrow (spacer keeps the panel centered) */}
      {isDesktop && (
        <div style={{ width: 48, flexShrink: 0 }}>
          {docs.length > 1 && index < docs.length - 1 && (
            <button
              onClick={() => goToPage(index + 1)}
              aria-label="Next want"
              style={sideArrowStyle}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Info row ─────────────────────────────────────────────────────────────

function InfoRow({ icon, label, value }) {
  return (
    <div
      style={{
        padding: 14,
        background: kCard,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          background: 'rgba(255,140,8,0.12)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 11 }}>
          {label}
        </div>
        <div
          style={{
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            marginTop: 2,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

// ── Money formatter ──────────────────────────────────────────────────────

function formatMoney(value) {
  const n =
    typeof value === 'number' ? value : Number.parseFloat(value) || 0;
  const str = Math.trunc(n).toString();
  let out = '';
  for (let i = 0; i < str.length; i++) {
    if (i > 0 && (str.length - i) % 3 === 0) out += ',';
    out += str[i];
  }
  return out;
}